import { useCallback, useRef, useState } from "react";

const MIN_THUMB_HEIGHT = 36;

export function useCustomScrollbar(scrollRef, { minThumbHeight = MIN_THUMB_HEIGHT } = {}) {
  const trackRef = useRef(null);
  const [scrollbar, setScrollbar] = useState({ visible: false, top: 0, height: 0 });
  const dragStateRef = useRef(null);
  const listenersRef = useRef({ move: null, up: null });

  const clearDragListeners = useCallback(() => {
    if (listenersRef.current.move) {
      window.removeEventListener("pointermove", listenersRef.current.move);
    }
    if (listenersRef.current.up) {
      window.removeEventListener("pointerup", listenersRef.current.up);
    }
    listenersRef.current = { move: null, up: null };
    dragStateRef.current = null;
  }, []);

  const updateScrollbar = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const hasOverflow = scrollHeight > clientHeight + 1;

    if (!hasOverflow || !track) {
      setScrollbar({ visible: false, top: 0, height: 0 });
      return;
    }

    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * trackHeight,
      minThumbHeight
    );
    const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
    const scrollRatio = scrollTop / Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = scrollRatio * maxThumbTop;

    setScrollbar({ visible: true, top: thumbTop, height: thumbHeight });
  }, [scrollRef, minThumbHeight]);

  const handleThumbPointerDown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      clearDragListeners();

      const trackHeight = track.clientHeight;
      const thumbHeight = Math.max(
        (el.clientHeight / el.scrollHeight) * trackHeight,
        minThumbHeight
      );

      dragStateRef.current = {
        startY: event.clientY,
        startScrollTop: el.scrollTop,
        trackHeight,
        thumbHeight,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      };

      const onPointerMove = (moveEvent) => {
        const drag = dragStateRef.current;
        const scrollEl = scrollRef.current;
        if (!drag || !scrollEl) return;

        const deltaY = moveEvent.clientY - drag.startY;
        const maxThumbTop = Math.max(drag.trackHeight - drag.thumbHeight, 0);
        const scrollable = Math.max(drag.scrollHeight - drag.clientHeight, 1);

        if (maxThumbTop <= 0) return;

        const nextScrollTop =
          drag.startScrollTop + (deltaY / maxThumbTop) * scrollable;
        scrollEl.scrollTop = Math.max(0, Math.min(nextScrollTop, scrollable));
        scrollEl.dispatchEvent(new Event("scroll", { bubbles: true }));
      };

      const onPointerUp = () => {
        clearDragListeners();
      };

      listenersRef.current = { move: onPointerMove, up: onPointerUp };
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [scrollRef, clearDragListeners, minThumbHeight]
  );

  const handleTrackPointerDown = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;

      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const rect = track.getBoundingClientRect();
      const clickY = event.clientY - rect.top;
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.max(
        (el.clientHeight / el.scrollHeight) * trackHeight,
        minThumbHeight
      );
      const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
      const scrollable = Math.max(el.scrollHeight - el.clientHeight, 1);

      if (maxThumbTop <= 0) {
        el.scrollTop = 0;
        el.dispatchEvent(new Event("scroll", { bubbles: true }));
        return;
      }

      const targetTop = Math.max(0, Math.min(clickY - thumbHeight / 2, maxThumbTop));
      el.scrollTop = (targetTop / maxThumbTop) * scrollable;
      el.dispatchEvent(new Event("scroll", { bubbles: true }));
    },
    [scrollRef, minThumbHeight]
  );

  return {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  };
}
